import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Referenzen() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/properties`).then((r) => setItems(r.data || []));
  }, []);

  return (
    <div data-testid="page-referenzen">
      <section className="px-6 md:px-12 lg:px-24 pt-32 md:pt-40 pb-12">
        <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
          <span className="inline-block w-2 h-2 bg-accent" />
          Portfolio
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] max-w-4xl">
          Aktuelle <em className="italic">Objekte</em> in Oberfranken.
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
          Eine Auswahl unserer abgeschlossenen und laufenden Sanierungen.
          Jedes Haus erzählt seine eigene Geschichte – und wir sind stolz,
          ein Kapitel davon weiterzuschreiben.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="referenzen-grid">
          {items.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p)}
              className="group text-left overflow-hidden relative aspect-[4/5] bg-muted/40"
              data-testid={`referenz-${p.id}`}
            >
              <img
                src={p.after_image}
                alt={p.title}
                style={p.id === "muenchberg-kulmbacher-strasse" ? { objectPosition: "35% center" } : undefined}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] mb-2 text-white/85 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 bg-accent" />
                  {p.location} · {p.year}
                </div>
                <div className="font-serif text-xl md:text-2xl lg:text-3xl font-light leading-tight">{p.title}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-white/70 capitalize">{p.status}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
          onClick={() => setActive(null)}
          data-testid="referenz-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-background max-w-5xl w-full max-h-[90vh] overflow-auto"
          >
            <img src={(active.gallery && active.gallery[0]) || active.after_image} alt={active.title} className="w-full h-[420px] object-contain bg-muted/40" />
            <div className="p-8 md:p-12">
              <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-3">
                <span className="inline-block w-2 h-2 bg-accent" />
                {active.location} · Baujahr {active.year}
              </div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4">{active.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{active.description}</p>

              {active.before_after && active.before_after.length > 0 && (
                <div className="mt-10">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-5">
                    Vorher / Nachher
                  </div>
                  <div className="space-y-10">
                    {active.before_after.map((pair, i) => (
                      <div key={pair.before} data-testid={`before-after-pair-${i}`}>
                        {pair.label && (
                          <h3 className="font-serif text-xl mb-3">{pair.label}</h3>
                        )}
                        <BeforeAfterSlider
                          beforeSrc={pair.before}
                          afterSrc={pair.after}
                          maxWidthClass="max-w-[408px]"
                          beforeAlt={`${active.title} – ${pair.label || "Raum"} vorher`}
                          afterAlt={`${active.title} – ${pair.label || "Raum"} nachher`}
                        />
                        <p className="mt-3 text-sm text-muted-foreground">
                          Ziehen Sie den Regler, um vorher und nachher zu vergleichen.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {active.features && active.features.length > 0 && (
                <div className="mt-8">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">Sanierungs-Highlights</div>
                  <ul className="space-y-2 text-sm">
                    {active.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <span className="mt-2 block w-3 h-px bg-accent shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {active.gallery && active.gallery.length > 1 && (
                <div className="mt-8">
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">Galerie</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {active.gallery.map((src, idx) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${active.title} – Bild ${idx + 1}`}
                        className="w-full h-48 object-cover"
                      />
                    ))}
                  </div>
                </div>
              )}

              {active.id === "muenchberg-kreuzbergstrasse" && (
                <div className="mt-8">
                  <Link
                    to="/referenzen/kreuzbergstrasse-haustuer"
                    onClick={() => setActive(null)}
                    data-testid="haustuer-story-link"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground h-12 px-7 text-sm tracking-wide hover:bg-primary/90 transition-colors"
                  >
                    Sanierungsgeschichte: Die neue Haustür ansehen
                    <span className="text-base">→</span>
                  </Link>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-2">
                {active.tags.map((t) => (
                  <span key={t} className="text-xs uppercase tracking-[0.2em] text-foreground/80 border border-border px-3 py-1.5">{t}</span>
                ))}
              </div>
              <button
                onClick={() => setActive(null)}
                className="mt-8 text-sm uppercase tracking-[0.2em] text-primary hover:underline"
                data-testid="referenz-modal-close"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
